import type { ColumnDef } from "@tanstack/react-table";
import type { FC } from "react";
import {
  PORTFOLIO_VALUATION_REASON_OBJ,
  PORTFOLIO_VALUATION_STATUS_OBJ,
  TRANSACTION_TYPE_OBJ,
} from "@signals/shared";
import { formatDisplayNumber } from "../_common";
import { formatCurrency } from "../_common";
import { isRecord } from "@signals/shared";
import type {
  MissingValuation,
  Position,
  TabRow,
  Transaction,
  ValuationStatus,
} from "./portfolio.hooks";
export const TABS = [
  "Positions",
  "Closed",
  "Transactions",
  "Income Events",
  "Cash Flow",
  "Exchange Rates",
  "Accounts",
  "Yearly Performance",
] as const;
export const PORTFOLIO_TAB_OBJ = {
  POSITIONS: TABS[0],
  CLOSED: TABS[1],
  TRANSACTIONS: TABS[2],
  INCOME_EVENTS: TABS[3],
  CASH_FLOW: TABS[4],
  EXCHANGE_RATES: TABS[5],
  ACCOUNTS: TABS[6],
  YEARLY_PERFORMANCE: TABS[7],
} as const;
export const PORTFOLIO_FILTER_COLUMN_OBJ = {
  ACCOUNT_ID: "accountId",
  DATE: "date",
} as const;
export const PORTFOLIO_MODAL = [
  "transaction",
  "importTransactions",
  "cashflow",
  "incomeEvent",
  "account",
  "symbol",
  "exchangeRate",
  "fetchRate",
] as const;
export type TPortfolioModal = (typeof PORTFOLIO_MODAL)[number];
export const PORTFOLIO_MODAL_OBJ = {
  TRANSACTION: PORTFOLIO_MODAL[0],
  IMPORT_TRANSACTIONS: PORTFOLIO_MODAL[1],
  CASHFLOW: PORTFOLIO_MODAL[2],
  INCOME_EVENT: PORTFOLIO_MODAL[3],
  ACCOUNT: PORTFOLIO_MODAL[4],
  SYMBOL: PORTFOLIO_MODAL[5],
  EXCHANGE_RATE: PORTFOLIO_MODAL[6],
  FETCH_RATE: PORTFOLIO_MODAL[7],
} as const;

export const today = () => new Date().toISOString().slice(0, 10);

export function formatNumber(v: number | undefined | null): string {
  if (v === undefined || v === null || !Number.isFinite(v)) return "—";
  return formatDisplayNumber(v, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function formatUSD(v: number | undefined | null): string {
  if (v === undefined || v === null || !Number.isFinite(v)) return "—";
  return formatCurrency(v, "USD");
}

export function formatPercent(v: number | undefined | null): string {
  if (v === undefined || v === null || !Number.isFinite(v)) return "—";
  return `${formatDisplayNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

export function numericValue(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

export function isTransaction(value: unknown): value is Transaction {
  if (!isRecord(value)) return false;
  const type = value["type"];
  const validType =
    type === TRANSACTION_TYPE_OBJ.BUY || type === TRANSACTION_TYPE_OBJ.SELL;
  return (
    validType &&
    typeof value["id"] === "string" &&
    (value["quantity"] === undefined || value["quantity"] === null || typeof value["quantity"] === "number") &&
    (value["blockedQuantity"] === undefined || typeof value["blockedQuantity"] === "number")
  );
}

export function isValuationIncomplete(value: {
  valuationStatus?: ValuationStatus | undefined;
  missingValuations?: MissingValuation[] | undefined;
} | null | undefined): boolean {
  return (
    value?.valuationStatus === PORTFOLIO_VALUATION_STATUS_OBJ.INCOMPLETE ||
    (value?.missingValuations?.length ?? 0) > 0
  );
}

export function positionHasIncompleteValuation(position: Position): boolean {
  return (
    isValuationIncomplete(position) ||
    position.marketValue == null ||
    position.marketValueUSD == null ||
    position.displayValue == null
  );
}

export function missingValuationReasonLabel(
  reason: MissingValuation["reason"],
): string {
  switch (reason) {
    case PORTFOLIO_VALUATION_REASON_OBJ.MISSING_MARKET_PRICE:
      return "Market price unavailable";
    case PORTFOLIO_VALUATION_REASON_OBJ.PENDING_CONVERSION:
      return "Currency conversion pending";
    case PORTFOLIO_VALUATION_REASON_OBJ.MISSING_FX_RATE:
      return "Exchange rate unavailable";
    default:
      return "Valuation unavailable";
  }
}

export function toTabRows<T extends object>(rows: T[]): TabRow[] {
  return rows.map((row) => Object.fromEntries(Object.entries(row)));
}

export function parseTransactionEdit(
  field: string,
  value: unknown,
): Partial<Transaction> | null {
  if (field === "note" && (typeof value === "string" || value === null)) {
    return { note: value };
  }
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return null;
  if (field === "quantity") return { quantity: parsed };
  if (field === "price") return { price: parsed };
  if (field === "commission") return { commission: parsed };
  return null;
}

export const rightNumber = (key: string, header: string): ColumnDef<Position> => ({
  accessorKey: key,
  header,
  meta: { align: "right" },
  cell: ({ getValue }) => formatNumber(numericValue(getValue())),
});

/** Display-currency column — formats value with the portfolio's display currency symbol. */
export const rightDisplayCurrency = (
  key: string,
  header: string,
  currency: string,
  dependsOnValuation = false,
): ColumnDef<Position> => ({
  accessorKey: key,
  header,
  meta: { align: "right" },
  cell: ({ getValue, row }) =>
    dependsOnValuation && positionHasIncompleteValuation(row.original)
      ? "—"
      : formatCurrency(numericValue(getValue()), currency),
});

export const rightPercent = (
  key: string,
  header: string,
  dependsOnValuation = false,
): ColumnDef<Position> => ({
  accessorKey: key,
  header,
  meta: { align: "right" },
  cell: ({ getValue, row }) =>
    dependsOnValuation && positionHasIncompleteValuation(row.original)
      ? "—"
      : formatPercent(numericValue(getValue())),
});


export interface MetricProps {
  label: string;
  value: string;
}

export const Metric: FC<MetricProps> = ({ label, value }) => (
  <div>
    <div className="text-sm text-muted-foreground">{label}</div>
    <div className="text-lg font-semibold">{value}</div>
  </div>
);

